import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useCallback } from "react";
import { z } from "zod";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
    Box,
    Typography,
    Chip,
    Skeleton,
    TablePagination,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import { useAuth } from "../../../auth";
import { customInstanceDocumentos } from "../../../api/axios-client";
import type { DoczBffOverviewDto, DoczDocumentDto } from "../../../api/documentos/model";
import type { FiltroAgrupador } from "../../../api/documentos/model";
import { useOverviewFilters } from "../../../api/documentos/endpoints";

import { DocumentsFilters, type DocumentsFiltersState } from "./components/DocumentsFilters";
import { DocumentListItem } from "./components/DocumentListItem";
import { FilterDrawer } from "./components/FilterDrawer";
import { useTranslation } from "react-i18next";

const documentsSearchSchema = z.object({
    page: z.number().catch(1),
    size: z.number().catch(10),
    palavraChave: z.string().optional(),
    area: z.string().optional(),
    tipoDocumento: z.string().optional(),
    dataInicialDe: z.string().optional(),
    dataInicialAte: z.string().optional(),
    dataFinalDe: z.string().optional(),
    dataFinalAte: z.string().optional(),
    sortField: z.enum(["creation", "registration", "documentType"]).optional(),
    sortDirection: z.enum(["newest", "oldest", "a-z", "z-a"]).optional(),
    searched: z.boolean().catch(false),
});

type DocumentsSearch = z.infer<typeof documentsSearchSchema>;
type SortField = NonNullable<DocumentsSearch["sortField"]>;
type SortDirection = NonNullable<DocumentsSearch["sortDirection"]>;

export const Route = createFileRoute("/__auth/documents/")({
    component: DocumentsIndexComponent,
    staticData: {
        title: "routes.documents",
    },
    validateSearch: (search) => documentsSearchSchema.parse(search),
});

function formatToISO8601StartOfDay(dateStr?: string): string | undefined {
    if (!dateStr) return undefined;
    return `${dateStr}T00:00:00.000Z`;
}

function formatToISO8601EndOfDay(dateStr?: string): string | undefined {
    if (!dateStr) return undefined;
    return `${dateStr}T23:59:59.000Z`;
}

function buildOverviewParams(tenant: string, search: DocumentsSearch): string {
    const params = new URLSearchParams();
    params.append("tenant", tenant);
    params.append("portalUrl", window.location.hostname);
    if (search.palavraChave) params.append("palavraChave", search.palavraChave);
    if (search.area) params.append("area", search.area);
    if (search.tipoDocumento) params.append("tipoDocumento", search.tipoDocumento);

    const isSebrae = window.constants?.cliente === "sebrae";

    if (isSebrae) {
        const dataInicialDeISO = formatToISO8601StartOfDay(search.dataInicialDe);
        const dataInicialAteISO = formatToISO8601EndOfDay(search.dataInicialAte);
        const dataFinalDeISO = formatToISO8601StartOfDay(search.dataFinalDe);
        const dataFinalAteISO = formatToISO8601EndOfDay(search.dataFinalAte);

        if (dataInicialDeISO) params.append("dataInicialDe", dataInicialDeISO);
        if (dataInicialAteISO) params.append("dataInicialAte", dataInicialAteISO);
        if (dataFinalDeISO) params.append("dataFinalDe", dataFinalDeISO);
        if (dataFinalAteISO) params.append("dataFinalAte", dataFinalAteISO);
    } else {
        const criadoDeISO = formatToISO8601StartOfDay(search.dataInicialDe);
        const criadoAteISO = formatToISO8601EndOfDay(search.dataInicialAte);
        const registradoDeISO = formatToISO8601StartOfDay(search.dataFinalDe);
        const registradoAteISO = formatToISO8601EndOfDay(search.dataFinalAte);

        if (criadoDeISO) params.append("criadoDe", criadoDeISO);
        if (criadoAteISO) params.append("criadoAte", criadoAteISO);
        if (registradoDeISO) params.append("registradoDe", registradoDeISO);
        if (registradoAteISO) params.append("registradoAte", registradoAteISO);
    }

    return params.toString();
}

function filterDocuments(
    documents: DoczDocumentDto[],
    search: DocumentsSearch,
    metadataFilters: Record<string, string[]>
): DoczDocumentDto[] {
    let filtered = documents;

    const activeMetadata = Object.entries(metadataFilters).filter(([, values]) => values.length > 0);
    if (activeMetadata.length > 0) {
        filtered = filtered.filter((doc) => {
            const docMeta = doc.metadados || {};
            return activeMetadata.every(([groupName, values]) =>
                values.includes(docMeta[groupName] || "")
            );
        });
    }

    return filtered;
}

function sortDocuments(
    documents: DoczDocumentDto[],
    sortField?: SortField | null,
    sortDir?: SortDirection | null
): DoczDocumentDto[] {
    if (!sortField || !sortDir) return documents;
    const sorted = [...documents];

    if (sortField === "documentType") {
        sorted.sort((a, b) => {
            const tA = (a.tipoDocumento || "").toLowerCase();
            const tB = (b.tipoDocumento || "").toLowerCase();
            return sortDir === "a-z" ? tA.localeCompare(tB) : tB.localeCompare(tA);
        });
    } else if (sortField === "registration") {
        sorted.sort((a, b) => {
            const dA = new Date(a.dataRegistro || 0).getTime();
            const dB = new Date(b.dataRegistro || 0).getTime();
            return sortDir === "newest" ? dB - dA : dA - dB;
        });
    } else if (sortField === "creation") {
        sorted.sort((a, b) => {
            const dA = new Date(a.dataCriacao || 0).getTime();
            const dB = new Date(b.dataCriacao || 0).getTime();
            return sortDir === "newest" ? dB - dA : dA - dB;
        });
    }
    return sorted;
}

function DocumentsIndexComponent() {
    const { t } = useTranslation();
    const { currentTenant } = useAuth();
    const tenant = currentTenant || "default-domain";
    const search = Route.useSearch();
    const navigate = useNavigate({ from: Route.fullPath });

    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [metadataFilters, setMetadataFilters] = useState<Record<string, string[]>>({});
     const [searchError, setSearchError] = useState(false);

    const [localFilters, setLocalFilters] = useState<DocumentsFiltersState>({
        search: search.palavraChave || "",
        area: search.area || null,
        type: search.tipoDocumento || null,
        dataInicialDe: search.dataInicialDe || "",
        dataInicialAte: search.dataInicialAte || "",
        dataFinalDe: search.dataFinalDe || "",
        dataFinalAte: search.dataFinalAte || "",
    });

    const queryString = useMemo(() => buildOverviewParams(tenant, search), [tenant, search]);

    const { data: overviewData, isLoading } = useQuery({
        queryKey: ["docz-overview", tenant, queryString],
        queryFn: async (): Promise<DoczBffOverviewDto> => {
            return customInstanceDocumentos<DoczBffOverviewDto>(
                `/api/v1/bff/docz-documentos/overview?${queryString}`,
                { method: "GET" }
            );
        },
        enabled: !!currentTenant && search.searched === true,
        placeholderData: keepPreviousData,
    });

    const allDocuments = overviewData?.conteudo || [];

    const { data: filtersData } = useOverviewFilters({
        tenant,
    }, {
        query: {
            enabled: !!tenant,
        }
    });

    const areaOptions = useMemo(() => {
        const rawData = (filtersData as any)?.data || filtersData;
        return (rawData?.area || []).filter(Boolean).sort();
    }, [filtersData]);

    const tipoDocumentoOptions = useMemo(() => {
        const rawData = (filtersData as any)?.data || filtersData;
        return (rawData?.tipoDocumento || []).filter(Boolean).sort();
    }, [filtersData]);

    const filteredDocuments = useMemo(
        () => filterDocuments(allDocuments, search, metadataFilters),
        [allDocuments, search, metadataFilters]
    );

    const sortedDocuments = useMemo(
        () => sortDocuments(filteredDocuments, search.sortField, search.sortDirection),
        [filteredDocuments, search.sortField, search.sortDirection]
    );

    const totalResults = sortedDocuments.length;
    const pageSize = search.size || 10;
    const currentPage = (search.page || 1) - 1;
    const paginatedDocuments = useMemo(() => {
        const start = currentPage * pageSize;
        return sortedDocuments.slice(start, start + pageSize);
    }, [sortedDocuments, currentPage, pageSize]);

    const filtros: FiltroAgrupador[] = useMemo(() => {
        if (allDocuments.length === 0) return [];
        const config: FiltroAgrupador[] = [];

        const metadataFields: Record<string, Record<string, number>> = {};
        allDocuments.forEach((doc) => {
            if (doc.metadados && typeof doc.metadados === "object") {
                Object.keys(doc.metadados).forEach((fieldName) => {
                    const fieldValue = (doc.metadados as Record<string, string>)[fieldName];
                    if (!fieldValue) return;
                    if (!metadataFields[fieldName]) metadataFields[fieldName] = {};
                    metadataFields[fieldName][fieldValue] = (metadataFields[fieldName][fieldValue] || 0) + 1;
                });
            }
        });

        Object.entries(metadataFields).forEach(([fieldName, valueCounts]) => {
            config.push({
                nome: fieldName,
                valores: Object.entries(valueCounts)
                    .map(([nome, total]) => ({ nome, total }))
                    .sort((a, b) => (a.nome || "").localeCompare(b.nome || "")),
            });
        });

        return config;
    }, [allDocuments]);

    const handleFilterChange = useCallback((key: keyof DocumentsFiltersState, value: string | null) => {
        setLocalFilters((prev) => ({ ...prev, [key]: value }));
        if (key === "search" && value) {
            setSearchError(false);
        }
    }, []);

    const handleSearch = useCallback(() => {
        if (!localFilters.search || localFilters.search.trim() === "") {
            setSearchError(true);
            return;
        }

        if (localFilters.dataInicialDe && localFilters.dataInicialAte && localFilters.dataInicialDe > localFilters.dataInicialAte) {
            alert(t("documents.validation.dataInicialInterval"));
            return;
        }
        if (localFilters.dataFinalDe && localFilters.dataFinalAte && localFilters.dataFinalDe > localFilters.dataFinalAte) {
            alert(t("documents.validation.dataFinalInterval"));
            return;
        }

        setSearchError(false);
        navigate({
            search: (prev) => ({
                ...prev,
                palavraChave: localFilters.search || undefined,
                area: localFilters.area || undefined,
                tipoDocumento: localFilters.type || undefined,
                dataInicialDe: localFilters.dataInicialDe || undefined,
                dataInicialAte: localFilters.dataInicialAte || undefined,
                dataFinalDe: localFilters.dataFinalDe || undefined,
                dataFinalAte: localFilters.dataFinalAte || undefined,
                page: 1,
                searched: true,
            }),
        });
    }, [navigate, localFilters, t]);

    const handleClearFilters = useCallback(() => {
        setSearchError(false);
        setMetadataFilters({});
        setLocalFilters({
            search: "",
            area: null,
            type: null,
            dataInicialDe: "",
            dataInicialAte: "",
            dataFinalDe: "",
            dataFinalAte: "",
        });
        navigate({
            search: {
                page: 1,
                size: search.size || 10,
                area: undefined,
                tipoDocumento: undefined,
                searched: false,
            },
        });
    }, [navigate, search.size]);

    const handlePageChange = (_: unknown, newPage: number) => {
        navigate({
            search: (prev) => ({ ...prev, page: newPage + 1 }),
            replace: true,
        });
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        navigate({
            search: (prev) => ({ ...prev, size: parseInt(e.target.value, 10), page: 1 }),
            replace: true,
        });
    };

    const handleSortChange = (field: SortField, direction: SortDirection) => {
        navigate({ search: (prev) => ({ ...prev, sortField: field, sortDirection: direction }) });
    };

    const handleMetadataFiltersApply = (filters: Record<string, string[]>) => {
        setMetadataFilters(filters);
    };

    return (
        <Box sx={{ p: 2, pb: 10 }}>
            <Box sx={{ mb: 3 }}>
                <DocumentsFilters
                    filters={localFilters}
                    onFilterChange={handleFilterChange}
                    onClear={handleClearFilters}
                    onSearch={handleSearch}
                    onOpenFilterDrawer={() => setFilterDrawerOpen(true)}
                    areaOptions={areaOptions}
                    tipoDocumentoOptions={tipoDocumentoOptions}
                    error={searchError}
                    isSearchPerformed={search.searched}
                />
            </Box>

            {search.searched && !isLoading && (
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        py: 0.5,
                        mb: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            whiteSpace: "nowrap",
                            mr: 1,
                        }}
                    >
                        exibindo{" "}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {totalResults} resultados
                        </Box>
                    </Typography>

                    <SortChip
                        label={t("common.creationDate")}
                        field="creation"
                        activeField={search.sortField}
                        activeDirection={search.sortDirection}
                        ascDirection="oldest"
                        descDirection="newest"
                        onToggle={handleSortChange}
                    />
                    <SortChip
                        label={t("documents.registrationDate")}
                        field="registration"
                        activeField={search.sortField}
                        activeDirection={search.sortDirection}
                        ascDirection="oldest"
                        descDirection="newest"
                        onToggle={handleSortChange}
                    />
                    <SortChip
                        label={t("documents.documentType")}
                        field="documentType"
                        activeField={search.sortField}
                        activeDirection={search.sortDirection}
                        ascDirection="a-z"
                        descDirection="z-a"
                        onToggle={handleSortChange}
                    />
                </Box>
            )}

            <Box>
                {!search.searched ? (
                    <Box sx={{ p: 6, textAlign: "center" }}>
                        <Typography variant="h6" color="text.secondary">
                            {t("documents.useFiltersHint")}
                        </Typography>
                    </Box>
                ) : isLoading ? (
                    <Box>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Skeleton key={i} variant="rectangular" height={72} sx={{ mb: 1, borderRadius: 1 }} />
                        ))}
                    </Box>
                ) : paginatedDocuments.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                        <Typography variant="h6" color="text.secondary">
                            Nenhum documento encontrado.
                        </Typography>
                    </Box>
                ) : (
                    paginatedDocuments.map((doc, index) => (
                        <DocumentListItem
                            key={doc.idInterno || index}
                            document={doc}
                        />
                    ))
                )}
            </Box>

            {search.searched && totalResults > 0 && (
                <TablePagination
                    component="div"
                    count={totalResults}
                    page={currentPage}
                    onPageChange={handlePageChange}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("common.itemsPerPage")}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{
                        mt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}
                />
            )}

            <FilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                filtros={filtros}
                selectedMetadataFilters={metadataFilters}
                onApply={handleMetadataFiltersApply}
            />
        </Box>
    );
}

interface SortChipProps {
    label: string;
    field: SortField;
    activeField?: SortField | null;
    activeDirection?: SortDirection | null;
    ascDirection: SortDirection;
    descDirection: SortDirection;
    onToggle: (field: SortField, direction: SortDirection) => void;
}

function SortChip({ label, field, activeField, activeDirection, ascDirection, descDirection, onToggle }: SortChipProps) {
    const isActive = activeField === field;
    const isAsc = isActive && activeDirection === ascDirection;

    const handleClick = () => {
        if (!isActive || !isAsc) {
            onToggle(field, ascDirection);
        } else {
            onToggle(field, descDirection);
        }
    };

    const icon = isActive
        ? isAsc
            ? <ArrowUpwardIcon fontSize="small" />
            : <ArrowDownwardIcon fontSize="small" />
        : undefined;

    return (
        <Chip
            label={label}
            icon={icon}
            onClick={handleClick}
            variant={isActive ? "filled" : "outlined"}
            color={isActive ? "primary" : "default"}
            size="small"
            sx={{ fontWeight: isActive ? 600 : 400 }}
        />
    );
}
